'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Modal, ModalBody, ModalHeader } from '@/components/ui/modal';
import type { IngredientWithDetails } from '@/lib/data/recipes';
import { GDA, gdaPct } from '@/lib/utils/gda';
import { joinAmount } from '@/lib/utils/format-amount';
import { resolveConsumedGrams } from '@/lib/utils/compute-nutrition';
import styles from './nutrition-breakdown-modal.module.css';

type MacroKey = 'calories' | 'carbs' | 'fat' | 'protein' | 'fibre';

const MACRO_TABS: { key: MacroKey; unit: string; gda: number }[] = [
  { key: 'calories', unit: 'kcal', gda: GDA.caloriesKcal },
  { key: 'carbs',    unit: 'g',    gda: GDA.carbsG },
  { key: 'fat',      unit: 'g',    gda: GDA.fatG },
  { key: 'protein',  unit: 'g',    gda: GDA.proteinG },
  { key: 'fibre',    unit: 'g',    gda: GDA.fiberG },
];

interface IngredientMacroRow {
  name: string;
  calories: number;
  carbs: number;
  fat: number;
  protein: number;
  fibre: number;
}

/**
 * `uncounted` names every ingredient that HAS an amount but could not be
 * weighed or has no nutrition data. It must be shown, not dropped: a missing
 * row reads as "contributes nothing", which is how a whole butternut squash
 * vanished from its own recipe's breakdown (#1030). An ingredient with no
 * amount at all ("salt to taste") has nothing to count and is left out.
 */
function computeBreakdown(
  ingredients: IngredientWithDetails[],
  servings: number,
): { rows: IngredientMacroRow[]; uncounted: string[] } {
  const effectiveServings = Math.max(1, servings);
  const rows: IngredientMacroRow[] = [];
  const uncounted: string[] = [];

  for (const ing of ingredients) {
    if (ing.displayQuantity == null && ing.shoppingQuantity == null) continue;
    if (ing.caloriesPer100g === null) {
      uncounted.push(ing.name);
      continue;
    }
    // Same grams policy as the server compute path (display pair preferred,
    // consumedFraction applied) — anything else drifts from the stored
    // per-serving totals shown in the nutrition panel.
    const grams = resolveConsumedGrams({
      displayQuantity: ing.displayQuantity,
      displayUnit: ing.displayUnitAbbrev,
      shoppingQuantity: ing.shoppingQuantity,
      shoppingUnit: ing.shoppingUnitAbbrev,
      densityGPerMl: ing.densityGPerMl,
      gPerPiece: ing.gPerPiece,
      consumedFraction: ing.consumedFraction,
    });
    if (grams === null) {
      uncounted.push(ing.name);
      continue;
    }

    const factor = grams / 100 / effectiveServings;
    rows.push({
      name:     ing.name,
      calories: ing.caloriesPer100g          * factor,
      carbs:    (ing.carbsGPer100g    ?? 0)  * factor,
      fat:      (ing.fatGPer100g      ?? 0)  * factor,
      protein:  (ing.proteinGPer100g  ?? 0)  * factor,
      fibre:    (ing.fiberGPer100g    ?? 0)  * factor,
    });
  }

  return { rows, uncounted };
}

function shareToColor(sharePct: number): string {
  const intensity = Math.max(0.08, sharePct / 100);
  return `color-mix(in oklch, var(--c-accent) ${Math.round(intensity * 100)}%, var(--c-surface-2))`;
}

interface NutritionBreakdownModalProps {
  ingredients: IngredientWithDetails[];
  servings: number;
  onClose: () => void;
}

export function NutritionBreakdownModal({
  ingredients,
  servings,
  onClose,
}: NutritionBreakdownModalProps) {
  const t = useTranslations('recipes.nutritionBreakdown');
  const tPanel = useTranslations('recipes.nutritionPanel');
  const [activeMacro, setActiveMacro] = useState<MacroKey>('calories');

  const { rows: allRows, uncounted } = computeBreakdown(ingredients, servings);
  const hasData = allRows.length > 0;

  const activeTab = MACRO_TABS.find((tab) => tab.key === activeMacro)!;
  const activeLabel = tPanel(activeMacro);

  // Sort and compute share for the active macro
  const sorted = [...allRows].sort((a, b) => b[activeMacro] - a[activeMacro]);
  const total = sorted.reduce((s, r) => s + r[activeMacro], 0);
  const rows = sorted.map((r) => ({
    ...r,
    sharePct: total > 0 ? (r[activeMacro] / total) * 100 : 0,
  }));

  const fmt = (v: number) => activeMacro === 'calories' ? Math.round(v).toString() : (Math.round(v * 10) / 10).toString();

  return (
    <Modal open onClose={onClose} size="md" labelledBy="nb-modal-title">
      <ModalHeader id="nb-modal-title" onClose={onClose}>
        {t('title')}
      </ModalHeader>
      <ModalBody>
        <p className={styles.subtitle}>{t('subtitle')}</p>

        <div className={styles.tabs} role="tablist" aria-label={t('macroNutrientAria')}>
          {MACRO_TABS.map((tab) => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={activeMacro === tab.key}
              className={`${styles.tab} ${activeMacro === tab.key ? styles.tabActive : ''}`}
              onClick={() => setActiveMacro(tab.key)}
              type="button"
            >
              {tPanel(tab.key)}
            </button>
          ))}
        </div>

        {!hasData ? (
          <p className={styles.emptyText}>{t('empty')}</p>
        ) : (
          <>
            <ul className={styles.list} aria-label={t('contributionsAria', { label: activeLabel })}>
              {rows.map((row) => (
                <li key={row.name} className={styles.row}>
                  <div className={styles.rowMeta}>
                    <span className={styles.ingredientName}>{row.name}</span>
                    <span className={styles.macroValue}>
                      {joinAmount(fmt(row[activeMacro]), activeTab.unit)}
                      <span className={styles.shareBadge}>{Math.round(row.sharePct)}%</span>
                    </span>
                  </div>
                  <div className={styles.barTrack} aria-hidden="true">
                    <div
                      className={styles.barFill}
                      style={{
                        width: `${row.sharePct}%`,
                        background: shareToColor(row.sharePct),
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>

            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>{t('total')}</span>
              <span className={styles.totalValue}>
                {joinAmount(fmt(total), activeTab.unit)}
                <span className={styles.totalGda}>
                  {gdaPct(total, activeTab.gda)}{tPanel('gdaSuffix')}
                </span>
              </span>
            </div>

            <p className={styles.disclaimer}>{t('disclaimer')}</p>
          </>
        )}

        {uncounted.length > 0 && (
          <p className={styles.uncounted}>
            {t('uncounted', { names: uncounted.join(', ') })}
          </p>
        )}
      </ModalBody>
    </Modal>
  );
}
